import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ChangeDetectionStrategy, ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { GtwElementsBaseUrlsModel } from '../../../shared/_models/gtw-elements-base-urls.model';
import { ReqHeaderElementsService } from './req-header-elements.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-efile-req-header-elements',
    templateUrl: './req-header-elements.component.html',
    styleUrls: ['./req-header-elements.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ReqHeaderElementsComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();
  /**
   * Emitted when the user clicks **Proceed** in the mandatory-header review
   * modal. Hosts (e.g. submit-action) listen for this to advance the legacy
   * submit chain (TEST → submit immediately, PRODUCTION → double-confirm).
   * Payload is the current submit envType when the host supplies one.
   */
  @Output() proceed = new EventEmitter<string>();

  /**
   * When set, the component reads its submission id and envType from these
   * inputs instead of `WorkspaceService.activeScreen`. Used by submit-action
   * which embeds the component inside its own modal overlay.
   */
  @Input() submissionIdOverride?: string;
  @Input() submitTypeOverride?: string;

  private subs = new Subscription();
  private isInitialized = false;

  baseURLs: GtwElementsBaseUrlsModel = { admin: '', api: '', custom: '', efile: '', pdfEngine: '', bulkPdfEngine: '' };
  globalService: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};
  GENERAL_CONFIG: any = {};
  rowData: any = {};
  filterParams: any = {};

  title: string = 'Review Mandatory Header Elements';
  loading: boolean = false;
  headerList: any[] = [];
  currentTheme: string = 'theme-black';
  minimise: boolean = false;

  constructor(
    private cdr: ChangeDetectorRef,
    private gtwSnackbarService: GtwSnackbarService,
    private reqHeaderElementsService: ReqHeaderElementsService,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
  ) { }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => this.applyWorkspaceContext()));
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => this.applyWorkspaceContext()));
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    this.isInitialized = false;
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const gc = generalConfigFromGlobal(this.gtwGlobal);
    this.baseURLs = {
      api: gc.api,
      admin: gc.admin,
      custom: gc.custom,
      efile: gc.efile,
      pdfEngine: gc.pdfEngine,
      bulkPdfEngine: gc.bulkPdfEngine,
    };
    const shell = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.USER_SETTINGS = shell.userSettingsData;
    this.SERVER_CONFIG = shell.serverConfigData;
    this.GENERAL_CONFIG = shell.generalConfigData;
    this.globalService = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
    this.filterParams = normalizeFilterParams(activeScreen);
    this.tryInit();
    this.cdr.markForCheck();
  }

  private tryInit(): void {
    if (this.isInitialized) {
      return;
    }
    if (!this.GENERAL_CONFIG) {
      return;
    }
    // When the host supplies an explicit submission id (submit-action chain),
    // we don't require a fully populated rowData payload — the workspace
    // selection may even be empty if the user navigated straight to the
    // submission via deep-link.
    if (
      !this.submissionIdOverride &&
      (!this.rowData || Object.keys(this.rowData).length === 0)
    ) {
      return;
    }
    this.isInitialized = true;
    this.init();
  }

  private init(): void {
    this.title = this.rowData.title || 'Review Mandatory Header Elements';
    this.currentTheme = this.USER_SETTINGS?.user?.selected_theme || 'theme-black';
    this.loadHeaderElements();
  }

  private loadHeaderElements(): void {
    this.loading = true;
    this.cdr.detectChanges();

    const submissionId =
      this.submissionIdOverride ||
      this.rowData.submission_id ||
      this.rowData.SUBMISSION_ID ||
      '';

    this.reqHeaderElementsService.getHeaderElements(
      this.GENERAL_CONFIG.base_url,
      submissionId,
      this.globalService || {},
      this.filterParams || {}
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.loading = false;
        if (response.jsonObject) {
          this.headerList = response.jsonObject;
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        this.loading = false;
        console.error('Error loading header elements:', error);
        this.cdr.detectChanges();
      }
    );
  }

  isNewGroup(index: number): boolean {
    if (index === 0) return false;
    return this.headerList[index].CHOICE_GROUP_ID !== this.headerList[index - 1].CHOICE_GROUP_ID;
  }

  showChoiceSeparator(index: number): boolean {
    if (index === 0) return false;
    return this.headerList[index].CHOICE_GROUP_ID === this.headerList[index - 1].CHOICE_GROUP_ID
      && this.headerList[index].CHOICE_SEQ !== this.headerList[index - 1].CHOICE_SEQ;
  }

  confirm(): void {
    // Hosts that drive submit-action embed this component as part of the
    // legacy `showReqHeaderEle` flow and react to `proceed`. Standalone
    // callers (legacy CE shell) still get the existing `request-grid-update`
    // refresh signal so their modal closes the same way it always did.
    this.proceed.emit(this.submitTypeOverride || '');
    this.requestGridUpdate.emit('refresh');
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }
}
